
/**
 * Module dependencies.
 */

import { ComponentType, ReactNode } from 'react';
import { RouterLink } from './router-link';
import { color } from 'react-components/styles';
import { switchProp, theme } from 'styled-tools';
import styled, { css } from 'styled-components';

/**
 * Export `LinkColorTheme` type.
 */

export type LinkColorTheme = 'primary' | 'secondary';

/**
 * `LinkProps` type.
 */

type LinkProps = {
  as?: string | ComponentType<any>;
  children?: ReactNode;
  className?: string;
  colorTheme?: LinkColorTheme;
  disabled?: boolean | undefined;
  href?: string;
  onClick?: () => void;
  rel?: string;
  role?: string;
  target?: string;
  to?: any;
};

/**
 * Export `Anchor` styled component.
 */

export const Anchor = styled.a<{ colorTheme: LinkColorTheme }>`
  border: none;
  cursor: pointer;
  outline: none;
  text-decoration: none;
  transition: ${theme('animations.defaultTransition')};
  transition-property: border-color, color;

  ${switchProp('colorTheme', {
    primary: css`
      color: ${color('primary')};

      &:active,
      &:visited {
        color: ${color('primary')};
      }

      &:focus-visible {
        border: 2px solid ${color('blue400')};
        color: ${color('blue600')};
      }

      &:focus,
      &:hover {
        color: ${color('blue600')};
      }

      &[aria-disabled=true] {
        color: ${color('blue200')};
      }
    `,
    secondary: css`
      color: ${color('gray500')};

      &:active,
      &:visited {
        color: ${color('gray500')};
      }

      &:focus-visible {
        border: 2px solid ${color('blue400')};
        color: ${color('gray600')};
      }

      &:focus,
      &:hover {
        color: ${color('gray600')};
      }

      &[aria-disabled=true] {
        color: ${color('gray400')};
      }
    `
  })}

  &[aria-disabled=true] {
    cursor: default;
    pointer-events: none;
  }
`;

/**
 * Export `Link` component.
 */

export function Link(props: LinkProps): JSX.Element {
  const { as, colorTheme = 'primary', disabled = false, href, onClick, rel, target, to, ...rest } = props;

  if (to) {
    return (
      <Anchor
        {...rest}
        as={RouterLink as ComponentType<any>}
        colorTheme={colorTheme}
        disabled={disabled}
        to={to}
      />
    );
  }

  return (
    <Anchor
      {...as ? { as } : {}}
      {...target ? { target } : {}}
      {...rel ? { rel } : {}}
      {...href ? { href } : {}}
      {...onClick ? { disabled, onClick } : {}}
      {...rest}
      aria-disabled={disabled}
      colorTheme={colorTheme}
    />
  );
}
